import { InventoryDataExtractor, InventoryElements } from '../../../common/inventory/extractors/InventoryDataExtractor';
import { ReportSummary } from '../../summary/services/ReportSummaryRepository';

export type FlatInventory = Record<
  string,
  Record<
    string,
    {
      currentCount?: number;
      previousCount?: number;
    }
  >
>;

function flattenInventory(inventoryElements: InventoryElements) {
  const { keys, mac, symmetricAlgorithms, asymmetricAlgorithms, operations, keyStores, hashFunctions } = inventoryElements;
  return (keys ?? [])
    .concat(mac ?? [])
    .concat(asymmetricAlgorithms ?? [])
    .concat(symmetricAlgorithms ?? [])
    .concat(operations ?? [])
    .concat(keyStores ?? [])
    .concat(hashFunctions ?? []);
}

function extractInventoryElements(summary: ReportSummary) {
  const inventoryDataExtractor = new InventoryDataExtractor();
  return inventoryDataExtractor.extractInventoryElements(summary.inventory);
}

function parseReportSummaries(currentReportSummary: ReportSummary, previousReportSummary: ReportSummary) {
  const currentInventoryElements = extractInventoryElements(currentReportSummary);
  const previousInventoryElements = extractInventoryElements(previousReportSummary);
  const currentFlatInventory = flattenInventory(currentInventoryElements);
  const previousFlatInventory = flattenInventory(previousInventoryElements);

  const mergedFlatInventory: FlatInventory = {};
  currentFlatInventory.forEach((inventory) => {
    const name = inventory.getName();
    const category = mergedFlatInventory[name] ?? {};

    inventory.getLines().forEach((line) => {
      const lineName = line.getName();
      const categorySubset = category[lineName] ?? {};
      category[lineName] = { ...categorySubset, currentCount: line.getCount() };
    });
    mergedFlatInventory[name] = category;
  });

  previousFlatInventory.forEach((inventory) => {
    const name = inventory.getName();
    const category = mergedFlatInventory[name] ?? {};
    inventory.getLines().forEach((line) => {
      const lineName = line.getName();
      const categorySubset = category[lineName] ?? {};
      category[lineName] = { ...categorySubset, previousCount: line.getCount() };
    });
    mergedFlatInventory[name] = category;
  });

  return mergedFlatInventory;
}

export class InventoryComparison {
  public readonly data: FlatInventory;

  constructor(currentReportSummary: ReportSummary, previousReportSummary: ReportSummary) {
    this.data = parseReportSummaries(currentReportSummary, previousReportSummary);
  }

  getCategoryTotals(category: string) {
    const dataCategory = this.data[category];
    let currentTotalCount = 0;
    let previousTotalCount = 0;

    if (!dataCategory) {
      return { currentTotalCount, previousTotalCount };
    }

    for (const counts of Object.values(dataCategory)) {
      currentTotalCount += counts.currentCount ?? 0;
      previousTotalCount += counts.previousCount ?? 0;
    }

    return { currentTotalCount, previousTotalCount };
  }
}
